const loaded = new Set<string>();

export function loadExternalScript(src: string): Promise<void> {
    if (loaded.has(src)) return Promise.resolve();
    return new Promise((resolve) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            loaded.add(src);
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => {
            loaded.add(src);
            if (src.includes('pdf.min.js') && (window as any).pdfjsLib) {
                (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = '/libs/pdf.worker.min.js';
            }
            resolve();
        };
        s.onerror = () => resolve();
        document.head.appendChild(s);
    });
}

export function loadToolDependencies(toolId: string) {
    if (!toolId) return;

    // PDF Tools & Converters
    if (
        toolId.includes('pdf') || 
        toolId === 'doc-to-pdf' || 
        toolId === 'excel-to-pdf' || 
        toolId === 'ppt-to-pdf' || 
        toolId === 'html-to-pdf' ||
        toolId === 'word-to-pdf'
    ) {
        loadExternalScript('/libs/FileSaver.min.js');
        loadExternalScript('/libs/pdf-lib.min.js');
        loadExternalScript('/libs/pdf.min.js');
        loadExternalScript('/libs/html2canvas.min.js');
        loadExternalScript('/libs/jszip.min.js');
    }

    // Zip & Multi-File Downloads
    if (
        toolId.includes('zip') || 
        toolId.includes('image') || 
        toolId === 'batch-file-renamer' || 
        toolId === 'pdf-extract-images' || 
        toolId === 'instagram-grid-splitter' || 
        toolId === 'polaroid-maker' || 
        toolId === 'image-collage-maker' ||
        toolId === 'csv-excel-processor' ||
        toolId === 'sitemap-generator' ||
        toolId === 'robots-txt-tool' ||
        toolId === 'tweet-to-image' ||
        toolId === 'rotate-image' ||
        toolId === 'flip-image'
    ) {
        loadExternalScript('/libs/FileSaver.min.js');
        loadExternalScript('/libs/jszip.min.js');
    }

    // Excel & CSV Tools
    if (toolId === 'excel-to-pdf' || toolId === 'csv-excel-processor' || toolId.includes('excel')) {
        loadExternalScript('/libs/xlsx.full.min.js');
        loadExternalScript('/libs/FileSaver.min.js');
    }

    // Word / Docx Tools
    if (toolId === 'doc-to-pdf' || toolId === 'text-to-word' || toolId === 'word-to-pdf') {
        loadExternalScript('/libs/mammoth.browser.min.js');
        loadExternalScript('/libs/docx.min.js');
        loadExternalScript('/libs/FileSaver.min.js');
    }

    // OCR Tools
    if (toolId === 'image-to-text' || toolId === 'pdf-ocr' || toolId === 'image-editor' || toolId.includes('ocr')) {
        loadExternalScript('/libs/tesseract.min.js');
    }

    // QR & Barcode Tools
    if (toolId === 'barcode-generator' || toolId.includes('barcode')) {
        loadExternalScript('/libs/jsbarcode.min.js');
    }
    if (toolId === 'qr-code-reader' || toolId.includes('qr')) {
        loadExternalScript('/libs/jsqr.js');
    }

    // PHP Pako compression
    if (toolId === 'php-decoder' || toolId === 'php-encoder') {
        loadExternalScript('/libs/pako.min.js');
    }

    // SVG Vectorizer
    if (toolId === 'image-to-svg') {
        loadExternalScript('/libs/imagetracer.js');
    }

    // Crypto
    if (toolId === 'duplicate-file-finder' || toolId === 'md5-hash' || toolId.includes('hash')) {
        loadExternalScript('/libs/crypto-js.min.js');
    }
}

export async function ensurePdfJs(): Promise<any> {
    if (typeof (window as any).pdfjsLib !== 'undefined') return (window as any).pdfjsLib;
    await loadExternalScript('/libs/pdf.min.js');
    if ((window as any).pdfjsLib) {
        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = '/libs/pdf.worker.min.js';
    }
    return (window as any).pdfjsLib;
}

export async function ensureJsZip(): Promise<any> {
    if (typeof (window as any).JSZip !== 'undefined') return (window as any).JSZip;
    await loadExternalScript('/libs/jszip.min.js');
    return (window as any).JSZip;
}

export async function ensureXlsx(): Promise<any> {
    if (typeof (window as any).XLSX !== 'undefined') return (window as any).XLSX;
    await loadExternalScript('/libs/xlsx.full.min.js');
    return (window as any).XLSX;
}

export async function ensureMammoth(): Promise<any> {
    if (typeof (window as any).mammoth !== 'undefined') return (window as any).mammoth;
    await loadExternalScript('/libs/mammoth.browser.min.js');
    return (window as any).mammoth;
}

export async function ensureDocx(): Promise<any> {
    if (typeof (window as any).docx !== 'undefined') return (window as any).docx;
    await loadExternalScript('/libs/docx.min.js');
    return (window as any).docx;
}

export async function ensureHtml2Canvas(): Promise<any> {
    if (typeof (window as any).html2canvas !== 'undefined') return (window as any).html2canvas;
    await loadExternalScript('/libs/html2canvas.min.js');
    return (window as any).html2canvas;
}

export async function ensureCryptoJs(): Promise<any> {
    if (typeof (window as any).CryptoJS !== 'undefined') return (window as any).CryptoJS;
    await loadExternalScript('/libs/crypto-js.min.js');
    return (window as any).CryptoJS;
}

export async function ensureImageTracer(): Promise<any> {
    if (typeof (window as any).ImageTracer !== 'undefined') return (window as any).ImageTracer;
    await loadExternalScript('/libs/imagetracer.js');
    return (window as any).ImageTracer;
}

export async function ensureTesseract(): Promise<any> {
    if (typeof (window as any).Tesseract !== 'undefined') return (window as any).Tesseract;
    await loadExternalScript('/libs/tesseract.min.js');
    return (window as any).Tesseract;
}

